import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "thresholdsContainer", "thresholdRow", "thresholdTemplate", "emptyMessage",
    "stepSizeRow", "position", "positionBadge", "destroyField",
    "maxValue", "stepSize", "thresholdMin", "thresholdMax",
    "polaritySection", "dualPolarityCheckbox",
    "assetLabelCol", "liabilityLabelCol", "assetLabelInput", "liabilityLabelInput",
    "assetLabelTitle", "liabilityLabelTitle", "assetLabelHint", "liabilityLabelHint"
  ]

  connect() {
    this.syncScaleLimits()
    this.syncLabelMode()
  }

  handleChange(event) {
    const { name } = event.target
    if (name === "dual_polarity_mode" || name === "tracker_scale[polarity]") {
      this.syncLabelMode()
    }
  }

  addThreshold() {
    const template = this.thresholdTemplateTarget.innerHTML
    const uniqueId = new Date().getTime()
    const newRow = template.replace(/NEW_RECORD/g, uniqueId)

    if (this.hasEmptyMessageTarget) {
      this.emptyMessageTarget.style.display = "none"
    }

    this.thresholdsContainerTarget.insertAdjacentHTML("beforeend", newRow)
    this.updatePositions()
    this.syncScaleLimits()
    this.syncLabelMode()
  }

  removeThreshold(event) {
    const row = event.target.closest("[data-scale-form-target='thresholdRow']")
    if (!row) return

    const destroyField = row.querySelector("[data-scale-form-target='destroyField']")
    if (destroyField && destroyField.name.includes("[id]")) {
      destroyField.value = "1"
      row.style.display = "none"
    } else {
      row.remove()
    }

    this.updatePositions()
  }

  nudgeModeChanged(event) {
    if (this.hasStepSizeRowTarget) {
      this.stepSizeRowTarget.style.display = event.target.value === "tier" ? "none" : ""
    }
  }

  maxValueChanged() {
    this.syncScaleLimits()
  }

  polarityChanged() {
    this.syncLabelMode()
  }

  dualPolarityChanged() {
    this.syncLabelMode()
  }

  syncLabelMode() {
    const dualMode = this.isDualPolarity()
    const polarity = this.selectedPolarity()

    if (this.hasPolaritySectionTarget) {
      this.polaritySectionTarget.classList.toggle("d-none", dualMode)
    }

    this.assetLabelColTargets.forEach((col) => {
      col.classList.toggle("d-none", !dualMode && polarity !== "standard")
    })

    this.liabilityLabelColTargets.forEach((col) => {
      col.classList.toggle("d-none", !dualMode && polarity !== "inverse")
    })

    this.assetLabelInputTargets.forEach((input) => {
      input.required = dualMode || polarity === "standard"
      input.disabled = !dualMode && polarity !== "standard"
    })

    this.liabilityLabelInputTargets.forEach((input) => {
      input.required = dualMode || polarity === "inverse"
      input.disabled = !dualMode && polarity !== "inverse"
    })

    this.assetLabelTitleTargets.forEach((title) => {
      title.textContent = dualMode ? "Asset label" : "Label"
    })

    this.liabilityLabelTitleTargets.forEach((title) => {
      title.textContent = dualMode ? "Liability label" : "Label"
    })

    this.assetLabelHintTargets.forEach((hint) => {
      hint.classList.toggle("d-none", dualMode || polarity !== "standard")
    })

    this.liabilityLabelHintTargets.forEach((hint) => {
      hint.classList.toggle("d-none", dualMode || polarity !== "inverse")
    })
  }

  isDualPolarity() {
    const checkbox = this.element.querySelector("#dual_polarity_mode")
    return checkbox ? checkbox.checked : false
  }

  selectedPolarity() {
    const selected = this.element.querySelector("input[name='tracker_scale[polarity]']:checked")
    return selected ? selected.value : "standard"
  }

  syncScaleLimits() {
    if (!this.hasMaxValueTarget) return

    const scaleMax = Math.max(1, parseInt(this.maxValueTarget.value, 10) || 1)

    if (this.hasStepSizeTarget) {
      this.stepSizeTarget.max = scaleMax
      const stepSize = parseInt(this.stepSizeTarget.value, 10)
      if (!Number.isNaN(stepSize) && stepSize > scaleMax) {
        this.stepSizeTarget.value = scaleMax
      }
    }

    this.thresholdMinTargets.forEach((field) => {
      field.max = scaleMax
    })

    this.thresholdMaxTargets.forEach((field) => {
      field.max = scaleMax
    })
  }

  updatePositions() {
    const visibleRows = this.thresholdRowTargets.filter(row => row.style.display !== "none")
    visibleRows.forEach((row, index) => {
      const positionField = row.querySelector("[data-scale-form-target='position']")
      const positionBadge = row.querySelector("[data-scale-form-target='positionBadge']")
      if (positionField) positionField.value = index
      if (positionBadge) positionBadge.textContent = index + 1
    })
  }
}
